// Création du héros (view.me.hasCharacter = false) : nom, classe (cartes défilantes), concept, histoire,
// couleur, sigle, posture d'absence, aperçu, puis envoi. En mode « rebirth », un héritier prend la suite
// du personnage mort. Les données d'affichage des classes sont recopiées d'ARCHITECTURE.md (sans import du moteur).
import { useEffect, useState } from "preact/hooks";
import { musicHint } from "../music";
import type { AttributeId, CharacterDraft, ClassId, ClientMessage, Posture } from "../../shared/types";
import { send, useAppState } from "../store";
import { Portrait } from "../components/Portrait";
import "./CharacterCreation.css";

export interface CharacterCreationProps { mode: "create" | "rebirth" }

interface ClassCard {
  id: ClassId;
  name: string;
  icon: string;
  /** couleur du médaillon et du filet de la carte */
  color: string;
  pitch: string;
  role: string;
  /** ordre d'attribution du tableau standard (priorité de la classe) */
  priority: AttributeId[];
  pv: string;
  mana: string;
  skills: string[];
  /** suggestions pour « Qui es-tu en une phrase ? » */
  concepts: string[];
}

const STANDARD_ARRAY = [15, 14, 13, 12, 10, 8];
const NAME_MIN = 2;
const NAME_MAX = 24;
const CONCEPT_MAX = 160;
const BACKSTORY_MAX = 600;
const STEP_LABELS = ["Ton nom", "Ta voie", "Ton âme"];

const CLASS_CARDS: ClassCard[] = [
  {
    id: "guerrier", name: "Guerrier", icon: "⚔️", color: "#8c3b2e",
    pitch: "Acier au poing et serment aux lèvres, il tient la ligne quand les autres reculent.",
    role: "Première ligne",
    priority: ["FOR", "CON", "DEX", "SAG", "CHA", "INT"],
    pv: "12 + CON", mana: "Aucun",
    skills: ["Athlétisme", "Intimidation", "Perception", "Survie"],
    concepts: [
      "Une ancienne sentinelle qui n’a jamais su abandonner son poste",
      "Un mercenaire las de se battre pour des causes qui ne sont pas les siennes",
      "Un vétéran qui a juré de protéger le prochain village",
      "Une forgeronne taciturne qui porte encore l’armure de son père",
      "Un garde du corps qui ne dort que d’un œil",
      "Un champion de tournoi tombé en disgrâce",
    ],
  },
  {
    id: "mage", name: "Mage", icon: "🔮", color: "#5b4bb5",
    pitch: "Il arrache aux vieux grimoires des formules qui font trembler les pierres.",
    role: "Savoir et sortilèges",
    priority: ["INT", "DEX", "CON", "SAG", "CHA", "FOR"],
    pv: "6 + CON", mana: "10 + 2 × INT",
    skills: ["Arcanes", "Histoire", "Investigation", "Médecine"],
    concepts: [
      "Une apprentie magicienne chassée de son académie",
      "Un érudit qui lit des livres que personne ne devrait lire",
      "Une alchimiste dont la dernière expérience a tourné très mal",
      "Un astrologue qui lit l’avenir dans la fumée des bougies",
      "Un ancien conseiller du roi qui a oublié la moitié de sa vie",
      "Une exilée qui cherche le grimoire à l’origine de sa chute",
    ],
  },
  {
    id: "rodeur", name: "Rôdeur", icon: "🏹", color: "#3f6b3a",
    pitch: "Il lit les traces dans la boue et connaît par leur nom chaque bête des forêts.",
    role: "Éclaireur et chasseur",
    priority: ["DEX", "SAG", "CON", "FOR", "INT", "CHA"],
    pv: "10 + CON", mana: "4",
    skills: ["Survie", "Nature", "Perception", "Discrétion"],
    concepts: [
      "Un chasseur solitaire qui suit la trace d’une bête légendaire",
      "Une guide des marais qui connaît chaque sentier",
      "Un ancien braconnier repenti qui veut racheter sa faute",
      "Une éclaireuse envoyée par un village menacé",
      "Un pisteur silencieux qui parle aux animaux",
      "Un trappeur qui traque le monstre ayant détruit sa ferme",
    ],
  },
  {
    id: "clerc", name: "Clerc", icon: "✨", color: "#b8862b",
    pitch: "Sa foi panse les blessures et repousse les ténèbres d’une voix posée.",
    role: "Soigneur et gardien sacré",
    priority: ["SAG", "CON", "FOR", "CHA", "INT", "DEX"],
    pv: "8 + CON", mana: "8 + 2 × SAG",
    skills: ["Médecine", "Intuition", "Histoire", "Persuasion"],
    concepts: [
      "Une prêtresse qui doute de sa foi depuis le dernier hiver",
      "Un moine guérisseur banni de son ordre",
      "Une missionnaire qui porte la lumière dans les ruines",
      "Un fossoyeur qui veille sur les morts comme sur les vivants",
      "Un ancien soldat devenu soigneur par remords",
      "Une veilleuse de la Lune qui entend la voix des esprits",
    ],
  },
  {
    id: "voleur", name: "Voleur", icon: "🗡️", color: "#3a3f4b",
    pitch: "Ombre parmi les ombres, il ouvre serrures et bourses sans jamais se faire voir.",
    role: "Furtif et opportuniste",
    priority: ["DEX", "INT", "CHA", "CON", "SAG", "FOR"],
    pv: "8 + CON", mana: "Aucun",
    skills: ["Discrétion", "Escamotage", "Acrobaties", "Tromperie", "Perception"],
    concepts: [
      "Une pickpocket au grand cœur qui ne vole que les menteurs",
      "Un contrebandier qui doit une fortune à la pègre",
      "Une cambrioleuse qui rembourse une dette à un vieux seigneur",
      "Un espion sans patrie qui a trahi tout le monde",
      "Un chapardeur qui se fait passer pour un marchand honnête",
      "Un escroc qui vient de jurer de ne plus mentir",
    ],
  },
  {
    id: "barde", name: "Barde", icon: "🎻", color: "#a8426b",
    pitch: "Sa voix tisse les légendes et ouvre les portes les plus closes.",
    role: "Charme et inspiration",
    priority: ["CHA", "DEX", "CON", "INT", "SAG", "FOR"],
    pv: "8 + CON", mana: "6 + 2 × CHA",
    skills: ["Représentation", "Persuasion", "Tromperie", "Intuition"],
    concepts: [
      "Un ménestrel qui raconte les histoires des autres",
      "Une chanteuse de taverne qui cache un secret",
      "Un conteur itinérant en quête de sa première grande légende",
      "Un troubadour qui connaît tous les potins du royaume",
      "Un poète qui a vendu sa voix à une créature de la nuit",
      "Une comédienne qui joue un rôle depuis trop longtemps",
    ],
  },
];

const PALETTE: { hex: string; name: string }[] = [
  { hex: "#d9662b", name: "Braise" },
  { hex: "#e8a33d", name: "Ambre" },
  { hex: "#f2d675", name: "Or pâle" },
  { hex: "#5f9e5a", name: "Mousse" },
  { hex: "#3fa7a0", name: "Sarcelle" },
  { hex: "#4a7fc1", name: "Cobalt" },
  { hex: "#7a5cc4", name: "Améthyste" },
  { hex: "#c85a8c", name: "Rose fané" },
  { hex: "#e9e2cf", name: "Ivoire" },
  { hex: "#8b8f9a", name: "Cendre" },
];

const SIGILS = [
  "🦊", "🐺", "🦉", "🐻", "🦅", "🐉", "🌙", "☀️", "⭐", "🔥", "❄️", "🌿",
  "🍀", "🌹", "🗝️", "⚓", "🎲", "🪶", "🛡️", "📜", "🍄", "🐚", "💎", "🕯️",
];

const POSTURES: { id: Posture; icon: string; name: string; text: string }[] = [
  { id: "prudent", icon: "🛡️", name: "Prudent", text: "se met à l’abri et se repose" },
  { id: "travailleur", icon: "🔨", name: "Travailleur", text: "gagne sa vie au village" },
  { id: "explorateur", icon: "🧭", name: "Explorateur", text: "fureter, au risque de mauvaises rencontres" },
];

const cardOf = (id: ClassId | null): ClassCard | null => CLASS_CARDS.find((c) => c.id === id) ?? null;

/** Les deux caractéristiques clés : le tableau standard attribué selon la priorité de la classe. */
const keyStats = (c: ClassCard): string => c.priority.slice(0, 2).map((a, i) => `${a} ${STANDARD_ARRAY[i]}`).join(" · ");

const scrollBehavior = (): ScrollBehavior =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ? "auto" : "smooth";

function ClassCarousel(props: { selected: ClassId | null; onPick: (id: ClassId) => void }) {
  return (
    <div class="character-creation-classes" role="radiogroup" aria-label="Ta classe">
      {CLASS_CARDS.map((c) => {
        const on = props.selected === c.id;
        return (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={on ? "true" : "false"}
            class={`character-creation-class${on ? " is-selected" : ""}`}
            style={`--card-accent: ${c.color}`}
            onClick={(e) => {
              props.onPick(c.id);
              e.currentTarget.scrollIntoView({ behavior: scrollBehavior(), inline: "center", block: "nearest" });
            }}
          >
            <Portrait classId={c.id} color={c.color} sigil={c.icon} size={84} />
            <span class="character-creation-class-name">{c.name}</span>
            <span class="character-creation-class-role">{c.role}</span>
            <span class="character-creation-class-pitch">{c.pitch}</span>
            <span class="character-creation-class-stats">
              <span class="character-creation-stat-label">Attributs</span>
              <span class="character-creation-stat-value">{keyStats(c)}</span>
              <span class="character-creation-stat-label">PV</span>
              <span class="character-creation-stat-value">{c.pv}</span>
              <span class="character-creation-stat-label">Mana</span>
              <span class="character-creation-stat-value">{c.mana}</span>
              <span class="character-creation-stat-label">Maîtrise</span>
              <span class="character-creation-stat-value">{c.skills.join(", ")}</span>
            </span>
            {on && <span class="chip chip-active character-creation-class-badge">Ta voie</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Si le serveur ne répond pas, le bouton d'envoi redevient disponible après ce délai. */
const SENDING_TIMEOUT_MS = 8_000;

export function CharacterCreation(props: CharacterCreationProps) {
  const app = useAppState();
  const view = app.view;
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [classId, setClassId] = useState<ClassId | null>(null);
  const [concept, setConcept] = useState("");
  const [backstory, setBackstory] = useState("");
  const [color, setColor] = useState(PALETTE[0].hex);
  const [sigil, setSigil] = useState(SIGILS[0]);
  const [posture, setPosture] = useState<Posture>("prudent");
  const [sending, setSending] = useState(false);

  // la musique prend la voix de la classe en cours de choix
  useEffect(() => { musicHint({ classId }); }, [classId]);
  useEffect(() => () => musicHint({}), []);

  // Une erreur renvoyée par le serveur rend les boutons disponibles à nouveau.
  useEffect(() => {
    setSending(false);
  }, [app.error]);

  useEffect(() => {
    if (!sending) return;
    const id = setTimeout(() => setSending(false), SENDING_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [sending]);

  if (!view) {
    return (
      <main class="character-creation">
        <p class="muted character-creation-wait">Le Conteur ouvre le registre…</p>
      </main>
    );
  }

  const card = cardOf(classId);
  const nameLen = name.trim().length;
  const nameOk = nameLen >= NAME_MIN && nameLen <= NAME_MAX;
  const conceptOk = concept.trim().length > 0;
  const ready = nameOk && card !== null && conceptOk;
  const dead = props.mode === "rebirth" ? view.graveyard[view.graveyard.length - 1] : undefined;
  const { world } = view;
  const title = props.mode === "rebirth" ? "Un héritier se lève" : "Crée ton héros";

  const goNext = () => setStep((s) => Math.min(2, s + 1));
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  function enter() {
    if (!ready || !card || sending) return;
    const draft: CharacterDraft = {
      name: name.trim(),
      classId: card.id,
      concept: concept.trim(),
      backstory: backstory.trim(),
      color,
      sigil,
      posture,
    };
    const msg: ClientMessage = props.mode === "rebirth" ? { t: "rebirth", draft } : { t: "create_character", draft };
    setSending(true);
    send(msg);
  }

  return (
    <main class="character-creation">
      <header class="character-creation-head">
        <p class="character-creation-kicker">{world.title ?? world.name}</p>
        <h1 class="character-creation-title">{title}</h1>
        {world.tagline && <p class="character-creation-tagline">{world.tagline}</p>}
      </header>

      {props.mode === "rebirth" && (
        <section class="card card-parchment character-creation-dead">
          <p class="character-creation-dead-text">
            {dead
              ? `${dead.name}, ${dead.className} de niveau ${dead.level}, a rendu son dernier souffle.`
              : "Un compagnon a rendu son dernier souffle."}
          </p>
          <p class="character-creation-dead-text">Tu hérites d’un de ses objets et de son expérience.</p>
        </section>
      )}

      {world.status === "genese" && (
        <p class="character-creation-genese" role="status">
          Le Conteur tisse encore le monde… tu peux déjà créer ton héros.
        </p>
      )}

      <ol class="character-creation-steps" aria-label="Étapes de création">
        {STEP_LABELS.map((label, i) => (
          <li
            key={label}
            class={`character-creation-step-tab${i === step ? " is-current" : i < step ? " is-done" : ""}`}
            aria-current={i === step ? "step" : undefined}
          >
            <span class="character-creation-step-dot">{i < step ? "✓" : i + 1}</span>
            <span>{label}</span>
          </li>
        ))}
      </ol>

      <div key={step} class="character-creation-step">
        {step === 0 && (
          <form
            class="character-creation-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (nameOk) goNext();
            }}
          >
            <h2 class="character-creation-h2">Comment t’appelles-tu ?</h2>
            <label class="label" for="cc-name">Le nom de ton héros</label>
            <input
              id="cc-name"
              class="input character-creation-name"
              type="text"
              autoComplete="off"
              maxLength={NAME_MAX}
              placeholder="Un prénom qui sonne"
              value={name}
              onInput={(e) => setName(e.currentTarget.value)}
            />
            <p class="muted character-creation-hint">
              {nameLen} / {NAME_MAX} · entre {NAME_MIN} et {NAME_MAX} caractères
            </p>
            <div class="character-creation-actions">
              <span />
              <button type="submit" class="btn btn-primary" disabled={!nameOk}>Continuer</button>
            </div>
          </form>
        )}

        {step === 1 && (
          <>
            <h2 class="character-creation-h2">Quelle voie suis-tu ?</h2>
            <p class="muted character-creation-lead">Glisse les cartes, puis touche celle qui te ressemble.</p>
            <ClassCarousel selected={classId} onPick={(id) => setClassId(id)} />
            <p class="character-creation-pick">
              {card ? `Tu as choisi le ${card.name.toLowerCase()}.` : "Choisis une voie pour continuer."}
            </p>
            <div class="character-creation-actions">
              <button type="button" class="btn btn-ghost" onClick={goBack}>Retour</button>
              <button type="button" class="btn btn-primary" disabled={!card} onClick={goNext}>Continuer</button>
            </div>
          </>
        )}

        {step === 2 && card && (
          <div class="character-creation-form">
            <h2 class="character-creation-h2">Qui es-tu, au fond ?</h2>

            <div class="character-creation-field">
              <label class="label" for="cc-concept">Qui es-tu en une phrase ?</label>
              <input
                id="cc-concept"
                class="input"
                type="text"
                autoComplete="off"
                maxLength={CONCEPT_MAX}
                placeholder="Une phrase qui te ressemble"
                value={concept}
                onInput={(e) => setConcept(e.currentTarget.value)}
              />
              <div class="character-creation-suggestions">
                {card.concepts.map((s) => (
                  <button key={s} type="button" class="chip character-creation-suggestion" onClick={() => setConcept(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div class="character-creation-field">
              <label class="label" for="cc-story">
                Ton histoire <span class="muted">(facultatif)</span>
              </label>
              <textarea
                id="cc-story"
                class="textarea character-creation-story"
                rows={5}
                maxLength={BACKSTORY_MAX}
                placeholder="D’où viens-tu ? Qui as-tu laissé derrière toi ? Qu’est-ce qui te hante, ou te fait sourire ?"
                value={backstory}
                onInput={(e) => setBackstory(e.currentTarget.value)}
              />
              <p class="muted character-creation-hint">{backstory.length} / {BACKSTORY_MAX}</p>
            </div>

            <fieldset class="character-creation-field">
              <legend class="label">Couleur de ton médaillon</legend>
              <div class="character-creation-palette" role="radiogroup" aria-label="Couleur de ton médaillon">
                {PALETTE.map((s) => {
                  const on = color === s.hex;
                  return (
                    <button
                      key={s.hex}
                      type="button"
                      role="radio"
                      aria-checked={on ? "true" : "false"}
                      aria-label={s.name}
                      title={s.name}
                      class={`character-creation-swatch${on ? " is-selected" : ""}`}
                      style={`background: ${s.hex}`}
                      onClick={() => setColor(s.hex)}
                    />
                  );
                })}
              </div>
            </fieldset>

            <fieldset class="character-creation-field">
              <legend class="label">Ton sigle</legend>
              <div class="character-creation-sigils" role="radiogroup" aria-label="Ton sigle">
                {SIGILS.map((g) => {
                  const on = sigil === g;
                  return (
                    <button
                      key={g}
                      type="button"
                      role="radio"
                      aria-checked={on ? "true" : "false"}
                      class={`character-creation-sigil${on ? " is-selected" : ""}`}
                      onClick={() => setSigil(g)}
                    >
                      {g}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <fieldset class="character-creation-field">
              <legend class="label">Quand tu n’es pas là, ton héros…</legend>
              <div class="character-creation-postures" role="radiogroup" aria-label="Posture d’absence">
                {POSTURES.map((p) => {
                  const on = posture === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="radio"
                      aria-checked={on ? "true" : "false"}
                      class={`character-creation-posture${on ? " is-selected" : ""}`}
                      onClick={() => setPosture(p.id)}
                    >
                      <span class="character-creation-posture-icon" aria-hidden="true">{p.icon}</span>
                      <span class="character-creation-posture-text">
                        <span class="character-creation-posture-name">{p.name}</span>
                        <span class="character-creation-posture-desc">{p.text}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <div class="card card-parchment character-creation-preview" aria-live="polite">
              <Portrait classId={card.id} color={color} sigil={sigil} size={112} />
              <div class="character-creation-preview-text">
                <span class="character-creation-preview-name">{name.trim() || "Sans nom"}</span>
                <span class="character-creation-preview-class">{card.name}</span>
                <span class="character-creation-preview-concept">{concept.trim() || "Ta phrase apparaîtra ici."}</span>
              </div>
            </div>

            {!conceptOk && (
              <p class="muted character-creation-hint">Écris une phrase pour te définir, ou touche une suggestion.</p>
            )}

            <div class="character-creation-actions">
              <button type="button" class="btn btn-ghost" onClick={goBack} disabled={sending}>Retour</button>
              <button
                type="button"
                class="btn btn-primary character-creation-enter"
                disabled={!ready || sending}
                onClick={enter}
              >
                {sending ? "Le monde s’ouvre…" : "Entrer dans le monde"}
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
